import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Link,
    Paper,
    InputLabel,
    CircularProgress,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import logo from '../assets/images/BackstageLogo.png'
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { signIn, getCurrentUser, fetchUserAttributes } from "@aws-amplify/auth";
import { showErrorToast, showSuccessToast } from "../toastUtils";
import axios from "axios";
import { useUser } from "../components/UserContext";

const LoginPage = () => {
    const { setCurrentUser } = useUser();
    const [showPassword, setShowPassword] = React.useState(false);
    const [formData, setFormData] = React.useState({
        email: "",
        password: "",
    });
    const [errors, setErrors] = React.useState({});
    const [touched, setTouched] = React.useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [isForgotPasswordDisabled, setIsForgotPasswordDisabled] =
        useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const checkUser = async () => {
            try {
                const currentUser = await getCurrentUser();
                if (currentUser) {
                    setIsAuthenticated(true);
                } else {
                    setIsAuthenticated(false);
                }
            } catch (error) {
                setIsAuthenticated(false);
            }
        };

        checkUser();
    }, []);

    useEffect(() => {
        if (isAuthenticated === true) {
            navigate("/");
        }
    }, [isAuthenticated, navigate]);


    const validateField = (name, value) => {
        switch (name) {
            case "email":
                if (!value) {
                    return "Email is required";
                }
                if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                    return "Invalid email address";
                }
                return "";

            case "password":
                if (!value) {
                    return "Password is required";
                }
                return "";

            default:
                return "";
        }
    };

    const handleSignInError = (error) => {
        if (error.name === "NotAuthorizedException") {
            setError("Incorrect username or password.");
        } else if (error.name === "UserNotConfirmedException") {
            setError("Please verify your email before logging in.");
        } else if (error.name === "UserNotFoundException") {
            setError("User does not exist.");
        } else if (axios.isCancel(error)) {
            setError("Request canceled. Please try again.");
        } else {
            setError("An error occurred during sign in. Please try again.");
        }
    };

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        const fieldValue = type === "checkbox" ? checked : value;

        setFormData((prev) => ({
            ...prev,
            [name]: fieldValue,
        }));

        if (touched[name]) {
            const fieldError = validateField(name, fieldValue);
            setErrors((prev) => ({
                ...prev,
                [name]: fieldError,
            }));
        }
    };

    const handleBlur = (event) => {
        const { name, value, type, checked } = event.target;
        const fieldValue = type === "checkbox" ? checked : value;

        setTouched((prev) => ({
            ...prev,
            [name]: true,
        }));

        const fieldError = validateField(name, fieldValue);
        setErrors((prev) => ({
            ...prev,
            [name]: fieldError,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const { email, password } = formData;

            if (!email || !password) {
                setError("Please fill in all required fields.");
                return;
            }

            const signInResponse = await signIn({ username: email, password });

            if (signInResponse.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                setIsForgotPasswordDisabled(true);
                navigate("/change-password", { state: { email } });
                return;
            }

            const data = await getCurrentUser();
            if (data?.userId) {
                localStorage.setItem("user_id", data.userId);
                // Only try to fetch attributes if we got past getCurrentUser
                const userAttributes = await fetchUserAttributes();
                setCurrentUser(userAttributes);
            }

            showSuccessToast("Logged in successfully!");
            navigate("/");
        } catch (error) {
            handleSignInError(error);
            showErrorToast(error.message);
            setCurrentUser(null)
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#F8F8F8",
            }}
        >
            {/* Form Section */}
            <Box
                sx={{
                    flexGrow: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 5
                }}
            >
                {/* Logo at the top */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <img src={logo} alt="logo" width="200" height="40" />
                </Box>
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 450,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    {/* Text above the form */}
                    <Typography
                        variant="h5"
                        sx={{
                            mb: 4,
                            color: "#5F5F5F",
                            fontWeight: 600,
                        }}
                    >
                        Sign in to your account
                    </Typography>

                    <Paper
                        elevation={2}
                        sx={{
                            width: "90%",
                            p: 4,
                            borderRadius: "12px",
                            backgroundColor: "white",
                            border: "none",
                        }}
                        variant="outlined"
                    >
                        <Box component="form" onSubmit={handleSubmit}>
                            <InputLabel shrink htmlFor="email" required>
                                User name or Email
                            </InputLabel>
                            <TextField
                                margin="none"
                                size="small"
                                required
                                fullWidth
                                name="email"
                                type="email"
                                autoComplete="email"
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && !!errors.email}
                                helperText={touched.email && errors.email}
                                sx={{ mb: 2 }}
                            />
                            <InputLabel shrink htmlFor="password" required>
                                Password
                            </InputLabel>
                            <TextField
                                margin="none"
                                size="small"
                                required
                                fullWidth
                                name="password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="current-password"
                                value={formData.password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.password && !!errors.password}
                                helperText={touched.password && errors.password}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                            >
                                                {showPassword ? (
                                                    <VisibilityIcon />
                                                ) : (
                                                    <VisibilityOffIcon />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end", // Align to the right
                                }}
                            >
                                <Link
                                    href="/forgot-password"
                                    sx={{
                                        color: 'main',
                                        textDecoration: "none",
                                        fontWeight: 500,
                                        fontSize: "16px",
                                    }}
                                    disabled={isForgotPasswordDisabled}
                                >
                                    Forgot password?
                                </Link>
                            </Box>

                            <Button
                                type="submit"
                                size="small"
                                fullWidth
                                variant="outlined"
                                sx={{
                                    mt: 2,
                                    mb: 2,
                                    "&:hover": {
                                        backgroundColor: "#FF6348",
                                        color: "white",
                                    },
                                    borderRadius: 1.5,
                                    border: "2px solid main",
                                    textTransform: "none",
                                    fontSize: "1rem",
                                    color: "#525455",
                                    fontWeight: 700,
                                }}
                                disabled={loading}
                            >
                                {!loading ? "Login" : <CircularProgress size={20} />}
                            </Button>
                            <Typography color="error">{error}</Typography>
                        </Box>
                        {/* <Link
                            sx={{
                                alignItems: "center",
                                display: "flex",
                                justifyContent: "center",
                                fontWeight: "bold",
                                cursor: "pointer",
                            }}
                            onClick={() => navigate("/sign-up")}
                        >
                            Sign Up
                        </Link> */}
                    </Paper>
                </Box>
            </Box>
        </Container>
    );
};

export default LoginPage;
